import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReceiptIndianRupee, Loader2, ShieldCheck } from "lucide-react";

const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/setup/logo`;

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    api.get("/setup/brand").then(({ data }) => {
      if (!data.configured) { navigate("/setup"); return; }
      setBrand(data);
    }).catch(() => setBrand({ trade_name: "", has_logo: false }));
  }, [navigate]);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/portal");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      if (u.must_change_password) navigate("/change-password");
      else navigate(u.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const name = brand?.trade_name || "Company";
  const location = [brand?.city, brand?.state].filter(Boolean).join(", ");

  const LogoMark = ({ size = "w-11 h-11", icon = "w-6 h-6", light }) =>
    brand?.has_logo ? (
      <div className={`${size} rounded-md bg-white border border-black/5 flex items-center justify-center overflow-hidden shrink-0`}>
        <img src={LOGO_URL} alt={name} className="w-full h-full object-contain p-1" />
      </div>
    ) : (
      <div className={`${size} rounded-md ${light ? "bg-white/10" : "bg-[#FF4500]"} flex items-center justify-center shrink-0`}>
        <ReceiptIndianRupee className={`${icon} ${light ? "text-white" : "text-white"}`} />
      </div>
    );

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: internal identity panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0A0A0A] text-white p-12">
        <div className="flex items-center gap-3">
          <LogoMark size="w-10 h-10" icon="w-5 h-5" light />
          <div className="leading-tight">
            <div className="font-display font-semibold">{brand ? name : "\u00a0"}</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">Internal Portal</div>
          </div>
        </div>

        <div>
          <h1 className="font-display text-4xl font-light leading-tight tracking-tight">
            Payroll &amp; payslips,<br /><span className="text-[#FF4500]">in one place.</span>
          </h1>
          <p className="text-white/55 mt-5 max-w-md leading-relaxed text-sm">
            The company system for running monthly payroll and issuing payslips.
            Team members sign in to view and download their own payslips.
          </p>
          <div className="flex items-center gap-2 mt-8 text-white/40 text-xs">
            <ShieldCheck className="w-4 h-4" />
            Authorised personnel only. Access is monitored.
          </div>
        </div>

        <div className="text-white/35 text-xs tabular">
          {location && <>{location} · </>}Financial year 1 April – 31 March · Asia/Kolkata
        </div>
      </div>

      {/* Right: sign-in */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <LogoMark size="w-9 h-9" icon="w-5 h-5" />
            <div className="leading-tight">
              <div className="font-display font-semibold">{brand ? name : "\u00a0"}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Internal Portal</div>
            </div>
          </div>

          <h2 className="font-display text-2xl font-semibold">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-8">
            Use your {name} work email and password.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="label-caps">Work email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required autoComplete="username" className="mt-1.5" data-testid="login-email-input"
                placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password" className="label-caps">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required autoComplete="current-password" className="mt-1.5" data-testid="login-password-input"
                placeholder="••••••••" />
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="login-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
